import { useState } from 'react';
import {
  Alert, Anchor, Badge, Box, Button, Card, Center, Code, CopyButton, Divider, Group, Image,
  Loader, Menu, Modal, Stack, Table, Text, Textarea, Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle, IconBan, IconClockExclamation, IconCopy, IconDotsVertical, IconDownload,
  IconExternalLink, IconEye, IconRefresh, IconSend, IconFileText,} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDate, formatDateTime, formatPhone, formatSom } from '../lib/format';
import { SILENT_ERROR } from '../lib/queryMeta';
import { SectionCard } from './SectionCard';

/**
 * SHARTNOMA — umumiy turlar, holat belgisi va amallar (YAGONA MANBA).
 *
 * Bu fayl shartnoma UI'sining "kutubxonasi": o'quvchi kartochkasidagi tab (`StudentContracts`),
 * `/contracts` sahifasi va shartnoma yaratuvchi shu yerdagi `ContractStatusBadge`,
 * `ContractRowActions` va `ContractDetailModal` ni ishlatadi — status ranglari va amallar
 * ikki joyda ikki xil bo'lib ketmasligi uchun.
 *
 * Holat mashinasi backend bilan bir xil (`backend/src/lib/contractSign.ts`):
 *   SENT → VIEWED → SIGNED (terminal) | REJECTED → (qayta yuborish) → SENT
 *   SENT/VIEWED/REJECTED → CANCELLED (terminal)
 * "EXPIRED" alohida status EMAS — server `isExpired` bayrog'ini hisoblab beradi.
 */

export type ContractStatus = 'SENT' | 'VIEWED' | 'SIGNED' | 'REJECTED' | 'CANCELLED';

/** Imzo qanday yo'l bilan qo'yilgan: ochiq havola yoki mobil ilova (audit). */
export type SignedVia = 'token' | 'app';

/** Ota-ona to'ldiradigan maydonlar (maktab ham oldindan to'ldirishi mumkin). */
export interface ContractParentData {
  parentFullName?: string;
  parentAddress?: string;
  passport?: string;
  jshshir?: string;
  parentPhone?: string;
}

/** Shartnomaning `data`/`schoolData` JSON'i — ota-ona maydonlari + shartlar. */
export interface ContractData extends ContractParentData {
  contractNumber?: string;
  studentFullName?: string;
  date?: string;
  monthlyPayment?: number;
  monthsCount?: number;
  fromDate?: string;
  toDate?: string;
}

/** Kim imzoladi / kim rad etdi — `parentData._audit` konverti (faqat admin ko'radi). */
export interface ContractAuditEntry {
  userId: string | null;
  role: string | null;
  ip: string | null;
  userAgent: string | null;
  via: SignedVia;
  at: string;
}

export interface ContractRow {
  id: string;
  token: string;
  /** Serverda generatsiya qilinadi; eski qatorlarda faqat `data.contractNumber` bo'lishi mumkin. */
  contractNumber: string | null;
  status: ContractStatus;
  /** OCHIQ havola orqali qolgan yuklab olishlar (ilova/admin uchun cheklov YO'Q). */
  remainingUses: number;
  createdAt: string;
  expiresAt: string | null;
  viewedAt: string | null;
  signerName: string | null;
  signedAt: string | null;
  rejectedAt: string | null;
  rejectReason: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  /** Server hisoblab beradi (`expiresAt` + status) — mijozda qayta hisoblanmaydi. */
  isExpired: boolean;
  link: string;
  data: ContractData | null;
  studentId: string | null;
  student?: { id: string; fullName: string; class?: { id: string; name: string } | null } | null;
  /**
   * Imzo yo'li ('token' = ochiq havola, 'app' = ilova). `GET /contracts` va
   * `GET /contracts/:id` javoblarining ikkalasida ham bor.
   * Ixtiyoriy qoldirilgan: imzolanmagan shartnomada `null` bo'ladi.
   */
  signedVia?: SignedVia | null;
}

/** `GET /contracts/:id` — to'liq kartochka (maktab nusxasi ↔ ota-ona nusxasi + audit). */
export interface ContractFull extends ContractRow {
  /** Maktab kiritgan ASL qiymatlar (ota-ona ustiga yozgandan keyin ham saqlanadi). */
  schoolData: ContractData | null;
  parentData: (ContractParentData & { _audit?: { sign?: ContractAuditEntry; reject?: ContractAuditEntry } }) | null;
  signature: string | null;
  signerIp: string | null;
  signerUserAgent: string | null;
  signedDocKey: string | null;
  signedDocSha256: string | null;
  signedDocAt: string | null;
  createdByName: string | null;
  cancelledByName: string | null;
}

/** Qayta yuborish/bekor qilish mumkin bo'lgan holatlar (SIGNED va CANCELLED — terminal). */
const REOPENABLE: ContractStatus[] = ['SENT', 'VIEWED', 'REJECTED'];

/** Ota-ona hali javob bermagan holatlar — faqat shular "muddati o'tishi" mumkin. */
export const PENDING_STATUSES: ContractStatus[] = ['SENT', 'VIEWED'];

export const CONTRACT_STATUSES: ContractStatus[] = ['SENT', 'VIEWED', 'SIGNED', 'REJECTED', 'CANCELLED'];

/**
 * Status → rang. Ma'nosi: kutilmoqda (sariq) → ota-ona ochdi (ko'k) → imzolandi (yashil);
 * rad etilgani qizil (e'tibor talab qiladi), bekor qilingani kulrang (yopilgan, xato emas).
 */
const STATUS_COLOR: Record<ContractStatus, string> = {
  SENT: 'yellow',
  VIEWED: 'blue',
  SIGNED: 'teal',
  REJECTED: 'red',
  CANCELLED: 'gray',
};

/** Mutatsiya xatosidan foydalanuvchi uchun matn (server javobi o'zbekcha). */
const errText = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

const DAY_MS = 86_400_000;

/**
 * Havola muddati (kun) — backend `lib/contractSign.ts` → `CONTRACT_TTL_DAYS` bilan BIR XIL.
 *
 * Server "oxirgi marta qachon yuborilgan" sanasini ALOHIDA ustunda saqlamaydi, lekin
 * YARATISHDA ham, QAYTA YUBORISHDA ham `expiresAt = hozir + CONTRACT_TTL_DAYS` qo'yadi
 * (`routes/contracts.ts` — ikkalasi ham `contractExpiresAt()`), ya'ni oxirgi yuborilgan
 * payt `expiresAt` dan aniq tiklanadi.
 * DIQQAT: backendda bu qiymat o'zgarsa, shu yerda ham o'zgarishi SHART.
 */
const CONTRACT_TTL_DAYS = 30;

/**
 * Javobsiz shartnoma shuncha kundan keyin "kechikkan" hisoblanadi.
 * YAGONA manba: `/contracts` sahifasidagi nishon ham, kutish kunlarining rangi ham
 * shundan oladi (avval "3" ikki joyda alohida yozilgan edi va ajralib ketishi mumkin edi).
 */
export const STALE_DAYS = 3;

/**
 * Shartnoma OXIRGI marta qachon yuborilgan.
 *
 * Qayta yuborishda (`POST /contracts/:id/resend`) yangi token va YANGI `expiresAt`
 * beriladi, `createdAt` esa O'ZGARMAYDI — shuning uchun kutish kunlarini `createdAt` dan
 * hisoblash qayta yuborilgan shartnomada JIMGINA katta (yolg'on) son ko'rsatardi.
 * `expiresAt` yo'q bo'lsa (eski qatorlar) yoki hisob yaratilgan sanadan oldinga tushsa —
 * `createdAt` ga qaytamiz.
 */
export function lastSentAt(c: { createdAt: string; expiresAt?: string | null }): Date {
  const created = new Date(c.createdAt);
  if (!c.expiresAt) return created;
  const sent = new Date(new Date(c.expiresAt).getTime() - CONTRACT_TTL_DAYS * DAY_MS);
  return Number.isFinite(sent.getTime()) && sent.getTime() > created.getTime() ? sent : created;
}

/** Ota-onaning kutish muddati (kun) — OXIRGI yuborilgan paytdan (qayta yuborish hisobga olinadi). */
export function waitingDays(c: { createdAt: string; expiresAt?: string | null }): number {
  const ms = Date.now() - lastSentAt(c).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / DAY_MS)) : 0;
}

/**
 * Shartnoma .docx faylini admin yo'li orqali yuklab oladi.
 * MUHIM: `/contracts/:id/document` ota-onaning `remainingUses` limitini YEMAYDI.
 * Xato javobi `responseType: 'blob'` sababli Blob bo'ladi — matnini o'qib chiqamiz.
 */
export async function downloadContractDocx(id: string, contractNumber?: string | null): Promise<void> {
  const res = await api.get(`/contracts/${id}/document`, { responseType: 'blob' });
  const cd = String(res.headers['content-disposition'] ?? '');
  const safe = (contractNumber || 'contract').replace(/[^a-zA-Z0-9-]/g, '');
  const name = /filename="?([^";]+)"?/i.exec(cd)?.[1] || `Shartnoma-${safe || 'contract'}.docx`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Blob javobli so'rov xatosidan matn (JSON xatosi Blob ichida keladi). */
async function blobErrText(e: unknown, fallback: string): Promise<string> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      return (JSON.parse(await data.text()) as { error?: string }).error || fallback;
    } catch {
      return fallback;
    }
  }
  return errText(e, fallback);
}

// ─────────────────────────────────────────────────────────────
//  Holat belgisi — HAMMA joyda shu ishlatiladi
// ─────────────────────────────────────────────────────────────

export function ContractStatusBadge({
  status,
  isExpired,
  size = 'sm',
}: {
  status: ContractStatus;
  /** Serverdan kelgan `isExpired` — "muddati o'tgan" qo'shimcha belgisi. */
  isExpired?: boolean;
  size?: string;
}) {
  const { t } = useTranslation();
  return (
    <Group gap={6} wrap="nowrap">
      <Badge color={STATUS_COLOR[status] ?? 'gray'} variant="light" size={size}>
        {t(`contractList.status${status}`, status)}
      </Badge>
      {isExpired && (
        <Tooltip label={t('contractList.expiredHint')} withArrow multiline w={260}>
          <Badge color="orange" variant="outline" size={size} leftSection={<IconClockExclamation size={12} />}>
            {t('contractList.expired')}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}

/** Imzo yo'li (ilova / ochiq havola). Ma'lumot bo'lmasa — chiziqcha. */
export function SignedViaLabel({ via }: { via?: SignedVia | null }) {
  const { t } = useTranslation();
  if (!via) return <Text size="sm" c="dimmed">—</Text>;
  return (
    <Badge variant="default" size="sm">
      {via === 'app' ? t('contractList.viaApp') : t('contractList.viaToken')}
    </Badge>
  );
}

// ─────────────────────────────────────────────────────────────
//  Amallar (qayta yuborish / bekor qilish / DOCX)
// ─────────────────────────────────────────────────────────────

/** `notified` — ilovadagi ota-onaga bildirishnoma ketdimi (SMS yo'q). */
interface ResendResult { link: string; notified: boolean }

/**
 * Bitta shartnoma qatori uchun amallar: batafsil, qayta yuborish, bekor qilish, DOCX.
 * Tasdiqlash modallari shu komponent ichida — chaqiruvchi sahifa ularni takrorlamaydi.
 */
export function ContractRowActions({
  contract,
  onDetails,
}: {
  contract: ContractRow;
  onDetails: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [resendOpen, setResendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [resendResult, setResendResult] = useState<ResendResult | null>(null);
  const [downloading, setDownloading] = useState(false);

  const reopenable = REOPENABLE.includes(contract.status);
  const number = contract.contractNumber ?? contract.data?.contractNumber ?? null;

  /** Ro'yxat ham (sahifa + o'quvchi tabi), ochiq kartochka ham yangilanadi. */
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['contracts'] });
    qc.invalidateQueries({ queryKey: ['contract', contract.id] });
  };

  const resendMut = useMutation({
    mutationFn: async () => (await api.post<ResendResult>(`/contracts/${contract.id}/resend`)).data,
    onSuccess: (data) => {
      setResendResult(data);
      refresh();
      notifications.show({
        color: data.notified ? 'green' : 'yellow',
        message: data.notified ? t('contractList.resendDone') : t('contractList.resendDoneNoApp'),
      });
    },
    onError: (e) => notifications.show({ color: 'red', message: errText(e, t('contractList.resendFailed')) }),
  });

  const cancelMut = useMutation({
    mutationFn: async () => (await api.post(`/contracts/${contract.id}/cancel`, { reason: reason.trim() })).data,
    onSuccess: () => {
      refresh();
      setCancelOpen(false);
      setReason('');
      notifications.show({ color: 'yellow', message: t('contractList.cancelDone') });
    },
    onError: (e) => notifications.show({ color: 'red', message: errText(e, t('contractList.cancelFailed')) }),
  });

  const download = async () => {
    setDownloading(true);
    try {
      await downloadContractDocx(contract.id, number);
    } catch (e) {
      notifications.show({ color: 'red', message: await blobErrText(e, t('contractList.downloadFailed')) });
    } finally {
      setDownloading(false);
    }
  };

  const closeResend = () => {
    setResendOpen(false);
    setResendResult(null);
  };

  const submitCancel = () => {
    if (reason.trim().length < 3) {
      setReasonError(t('contractList.cancelReasonRequired'));
      return;
    }
    setReasonError(null);
    cancelMut.mutate();
  };

  return (
    <>
      <Group gap={4} wrap="nowrap" justify="flex-end">
        <Button size="compact-sm" variant="subtle" leftSection={<IconEye size={14} />} onClick={onDetails}>
          {t('contractList.details')}
        </Button>
        <Menu withinPortal position="bottom-end" shadow="md">
          <Menu.Target>
            <Button size="compact-sm" variant="subtle" color="gray" px={6} aria-label={t('common.actions')}>
              <IconDotsVertical size={16} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconDownload size={14} />}
              disabled={downloading}
              onClick={() => void download()}
            >
              {t('contractList.download')}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconRefresh size={14} />}
              disabled={!reopenable}
              onClick={() => setResendOpen(true)}
            >
              {t('contractList.resend')}
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconBan size={14} />}
              disabled={!reopenable}
              onClick={() => setCancelOpen(true)}
            >
              {t('contractList.cancelAction')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Qayta yuborish — yangi token + yangi muddat, ilovaga bildirishnoma qayta ketadi */}
      <Modal opened={resendOpen} onClose={closeResend} title={t('contractList.resendTitle')} centered>
        {resendResult ? (
          <Stack>
            <Alert
              color={resendResult.notified ? 'green' : 'yellow'}
              variant="light"
              title={t('contractList.resendNewLink')}
            >
              {/* Ota-ona akkaunti bog'lanmagan bo'lsa bildirishnoma bormaydi — xodimga
                  yangi havolani o'zi ulashishi kerakligi aniq aytiladi. */}
              {resendResult.notified ? t('contractList.resendNotified') : t('contractList.resendNotNotified')}
              <Group mt="sm" gap="xs" wrap="nowrap">
                <Code style={{ flex: 1, wordBreak: 'break-all' }}>{resendResult.link}</Code>
                <CopyLinkButton value={resendResult.link} />
              </Group>
            </Alert>
            <Group justify="flex-end">
              <Button onClick={closeResend}>{t('contractGen.close')}</Button>
            </Group>
          </Stack>
        ) : (
          <Stack>
            <Text size="sm" c="dimmed">{t('contractList.resendHint')}</Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={closeResend}>{t('common.cancel')}</Button>
              <Button
                loading={resendMut.isPending}
                leftSection={<IconSend size={16} />}
                onClick={() => resendMut.mutate()}
              >
                {t('contractList.resend')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Bekor qilish — havola darhol o'ladi, sabab ota-onaga ko'rsatiladi */}
      <Modal opened={cancelOpen} onClose={() => setCancelOpen(false)} title={t('contractList.cancelTitle')} centered>
        <Stack>
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
            {t('contractList.cancelHint')}
          </Alert>
          <Textarea
            label={t('contractList.cancelReasonLabel')}
            placeholder={t('contractList.cancelReasonPlaceholder')}
            autosize
            minRows={3}
            maxLength={500}
            required
            value={reason}
            error={reasonError}
            onChange={(e) => { setReason(e.currentTarget.value); setReasonError(null); }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCancelOpen(false)}>{t('common.cancel')}</Button>
            <Button color="red" loading={cancelMut.isPending} leftSection={<IconBan size={16} />} onClick={submitCancel}>
              {t('contractList.cancelAction')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function CopyLinkButton({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <CopyButton value={value}>
      {({ copied, copy }) => (
        <Button size="xs" variant="light" color={copied ? 'teal' : 'brand'} leftSection={<IconCopy size={14} />} onClick={copy}>
          {copied ? t('contractGen.copied') : t('contractGen.copy')}
        </Button>
      )}
    </CopyButton>
  );
}

// ─────────────────────────────────────────────────────────────
//  To'liq kartochka (modal)
// ─────────────────────────────────────────────────────────────

/** Maktab ↔ ota-ona solishtiruvidagi maydonlar (faqat ota-ona tahrirlay oladiganlari). */
const COMPARE_FIELDS: { key: keyof ContractParentData; labelKey: string; phone?: boolean }[] = [
  { key: 'parentFullName', labelKey: 'fields.fullName' },
  { key: 'passport', labelKey: 'contractGen.passport' },
  { key: 'jshshir', labelKey: 'contractGen.jshshir' },
  { key: 'parentAddress', labelKey: 'contractList.address' },
  { key: 'parentPhone', labelKey: 'fields.phone', phone: true },
];

export function ContractDetailModal({
  id,
  opened,
  onClose,
}: {
  id: string | null;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: full, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['contract', id],
    queryFn: async () => (await api.get<ContractFull>(`/contracts/${id}`)).data,
    enabled: !!id && opened,
    meta: SILENT_ERROR, // xato modal ichida ochiq ko'rsatiladi
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('contractList.detailsTitle')} size="lg" centered>
      {isLoading ? (
        <Center h={180}><Loader /></Center>
      ) : isError || !full ? (
        <Stack>
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')}>
            {t('contractList.detailLoadError')}
          </Alert>
          <Group justify="flex-end">
            <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
              {t('contractList.retry')}
            </Button>
          </Group>
        </Stack>
      ) : (
        <ContractDetailBody full={full} />
      )}
    </Modal>
  );
}

function ContractDetailBody({ full }: { full: ContractFull }) {
  const { t } = useTranslation();
  const number = full.contractNumber ?? full.data?.contractNumber ?? '—';
  const school = full.schoolData ?? full.data ?? {};
  const parent = full.parentData ?? null;
  const audit = parent?._audit ?? null;
  const terms = full.data ?? {};

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Text fw={700}>№ {number}</Text>
        <ContractStatusBadge status={full.status} isExpired={full.isExpired} />
      </Group>

      {full.student && (
        <Row
          label={t('contractList.student')}
          value={`${full.student.fullName}${full.student.class ? ` · ${full.student.class.name}` : ''}`}
        />
      )}
      <Row label={t('contractList.created')} value={formatDateTime(full.createdAt)} />
      {full.createdByName && <Row label={t('contractList.createdBy')} value={full.createdByName} />}
      <Row label={t('contractList.viewedAt')} value={full.viewedAt ? formatDateTime(full.viewedAt) : t('contractList.notViewed')} />
      <Row label={t('contractList.expiresAt')} value={formatDateTime(full.expiresAt)} />

      {full.status === 'SIGNED' && (
        <>
          <Row label={t('contractList.signedAt')} value={formatDateTime(full.signedAt)} />
          <Row label={t('contractList.signer')} value={full.signerName ?? '—'} />
          <Row
            label={t('contractList.signedVia')}
            value={full.signedVia === 'app' ? t('contractList.viaApp') : full.signedVia === 'token' ? t('contractList.viaToken') : '—'}
          />
        </>
      )}
      {full.status === 'REJECTED' && (
        <>
          <Row label={t('contractList.rejectedAt')} value={formatDateTime(full.rejectedAt)} />
          <Row label={t('contractList.rejectReason')} value={full.rejectReason || '—'} />
        </>
      )}
      {full.status === 'CANCELLED' && (
        <>
          <Row label={t('contractList.cancelledAt')} value={formatDateTime(full.cancelledAt)} />
          <Row label={t('contractList.cancelReason')} value={full.cancelReason || '—'} />
          {full.cancelledByName && <Row label={t('contractList.cancelledBy')} value={full.cancelledByName} />}
        </>
      )}

      <Divider label={t('contractList.terms')} labelPosition="left" />
      <Row label={t('contractGen.contractDate')} value={formatDate(terms.date)} />
      <Row label={t('studentDetailPage.monthlyAmount')} value={formatSom(terms.monthlyPayment)} />
      <Row label={t('contractGen.durationMonths')} value={terms.monthsCount ? String(terms.monthsCount) : '—'} />
      <Row
        label={t('contractList.period')}
        value={terms.fromDate || terms.toDate ? `${formatDate(terms.fromDate)} — ${formatDate(terms.toDate)}` : '—'}
      />

      {/* Maktab kiritgan ASL qiymatlar ↔ ota-ona kiritgani YONMA-YON —
          "nizoda maktab nima yozgan edi?" savoliga javob. */}
      <Divider label={t('contractList.compareTitle')} labelPosition="left" />
      {!parent && (
        <Alert color="gray" variant="light" py="xs">{t('contractList.notFilled')}</Alert>
      )}
      <Table.ScrollContainer minWidth={420}>
        <Table verticalSpacing={6} withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w="26%">{t('contractList.field')}</Table.Th>
              <Table.Th>{t('contractList.schoolValue')}</Table.Th>
              <Table.Th>{t('contractList.parentValue')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {COMPARE_FIELDS.map((f) => {
              const s = (school[f.key] ?? '') as string;
              const p = (parent?.[f.key] ?? '') as string;
              const changed = !!p && !!s && p.trim() !== s.trim();
              const show = (v: string) => (v ? (f.phone ? formatPhone(v) : v) : '—');
              return (
                <Table.Tr key={f.key}>
                  <Table.Td><Text size="sm" c="dimmed">{t(f.labelKey)}</Text></Table.Td>
                  <Table.Td><Text size="sm">{show(s)}</Text></Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={changed ? 600 : 400} c={changed ? 'orange.7' : undefined}>{show(p)}</Text>
                      {changed && <Badge size="xs" color="orange" variant="light">{t('contractList.changed')}</Badge>}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Divider label={t('contractList.signature')} labelPosition="left" />
      {full.signature ? (
        <Box p="xs" style={{ border: '1px dashed var(--mantine-color-gray-4)', borderRadius: 10, background: '#fff' }}>
          <Image src={full.signature} alt={t('contractList.signature')} h={90} w="auto" fit="contain" />
        </Box>
      ) : (
        <Text size="sm" c="dimmed">{t('contractList.signatureNone')}</Text>
      )}

      {/* Audit — huquqiy dalil: kim, qayerdan, qaysi qurilmadan. */}
      <Divider label={t('contractList.auditTitle')} labelPosition="left" />
      <Row label="IP" value={full.signerIp || audit?.sign?.ip || '—'} />
      <Row label={t('contractList.device')} value={full.signerUserAgent || audit?.sign?.userAgent || '—'} />
      {audit?.reject && (
        <Row
          label={t('contractList.rejectedVia')}
          value={`${audit.reject.via === 'app' ? t('contractList.viaApp') : t('contractList.viaToken')} · ${formatDateTime(audit.reject.at)}`}
        />
      )}
      <Row
        label={t('contractList.frozenCopy')}
        value={full.signedDocKey ? t('contractList.frozenYes', { at: formatDateTime(full.signedDocAt) }) : t('contractList.frozenNo')}
      />
      {full.signedDocSha256 && <Row label="SHA-256" value={`${full.signedDocSha256.slice(0, 16)}…`} />}
      <Row label={t('contractList.downloadsLeft')} value={String(full.remainingUses)} />
      <Text size="xs" c="dimmed">{t('contractList.downloadsLeftHint')}</Text>

      <Divider label={t('contractList.link')} labelPosition="left" />
      <Group gap="xs" wrap="nowrap">
        <Code style={{ flex: 1, wordBreak: 'break-all' }}>{full.link}</Code>
        <CopyLinkButton value={full.link} />
      </Group>
      <Anchor href={full.link} target="_blank" rel="noopener noreferrer" size="sm">
        <Group gap={4} component="span"><IconExternalLink size={14} />{t('contractList.openLink')}</Group>
      </Anchor>
      {/* Yuridik ogohlantirish: imzo ota-onaning shaxsiy harakati bo'lishi shart */}
      <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} py="xs">
        {t('contractList.openLinkWarn')}
      </Alert>
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="sm" wrap="nowrap" align="flex-start">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>{label}</Text>
      <Text size="sm" fw={500} ta="right" style={{ wordBreak: 'break-word' }}>{value}</Text>
    </Group>
  );
}

// ─────────────────────────────────────────────────────────────
//  O'quvchi kartochkasidagi tab
// ─────────────────────────────────────────────────────────────

/** O'quvchi kartochkasidagi shartnomalar ro'yxati — holat, amallar va to'liq kartochka. */
export function StudentContracts({ studentId }: { studentId: string }) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: rows, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['contracts', studentId],
    queryFn: async () => (await api.get<ContractRow[]>('/contracts', { params: { studentId } })).data,
    meta: SILENT_ERROR, // xato ro'yxat o'rnida ochiq ko'rsatiladi
  });

  if (isLoading) return <Center h={120}><Loader /></Center>;

  // XATO HOLATI: avval 500 kelganda ham "Shartnoma yaratilmagan" degan YOLG'ON bo'sh
  // holat chiqardi — endi xato ochiq ko'rsatiladi va qayta urinish tugmasi bor.
  if (isError) {
    return (
      <Card>
        <Stack align="center" gap="sm" py="md">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
            {t('contractList.loadError')}
          </Alert>
          <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
            {t('contractList.retry')}
          </Button>
        </Stack>
      </Card>
    );
  }

  if (!rows?.length) {
    return (
      <SectionCard title={t('common.list')} icon={IconFileText} p={0}>
        <Center h={100}><Text c="dimmed">{t('contractList.empty')}</Text></Center>
      </SectionCard>
    );
  }

  return (
    <>
      <SectionCard title={t('common.list')} icon={IconFileText} p={0}>
        <Table.ScrollContainer minWidth={680}>
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('contractList.number')}</Table.Th>
                <Table.Th>{t('fields.status')}</Table.Th>
                <Table.Th>{t('contractList.created')}</Table.Th>
                <Table.Th>{t('contractList.answeredAt')}</Table.Th>
                <Table.Th w={190} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td fw={500}>{c.contractNumber ?? c.data?.contractNumber ?? '—'}</Table.Td>
                  <Table.Td><ContractStatusBadge status={c.status} isExpired={c.isExpired} /></Table.Td>
                  <Table.Td>{formatDate(c.createdAt)}</Table.Td>
                  <Table.Td><AnsweredAt contract={c} /></Table.Td>
                  <Table.Td>
                    <ContractRowActions contract={c} onDetails={() => setOpenId(c.id)} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </SectionCard>

      <ContractDetailModal id={openId} opened={!!openId} onClose={() => setOpenId(null)} />
    </>
  );
}

/** Javob sanasi: imzolangan / rad etilgan / bekor qilingan payt (yoki kutish muddati). */
export function AnsweredAt({ contract }: { contract: ContractRow }) {
  const { t } = useTranslation();
  if (contract.status === 'SIGNED') return <>{formatDateTime(contract.signedAt)}</>;
  if (contract.status === 'REJECTED') return <>{formatDateTime(contract.rejectedAt)}</>;
  if (contract.status === 'CANCELLED') return <>{formatDateTime(contract.cancelledAt)}</>;
  // Hisob OXIRGI yuborilgan paytdan yuriladi (qayta yuborilgan bo'lsa — o'shandan).
  const days = waitingDays(contract);
  const stale = days >= STALE_DAYS;
  return (
    <Text size="sm" c={stale ? 'orange.7' : 'dimmed'} fw={stale ? 600 : 400}>
      {t('contractList.waitingDays', { n: days })}
    </Text>
  );
}
